import { supabase } from '../config/supabase';
import { TMDB_API_KEY, TMDB_BASE_URL, TMDB_IMG_BASE } from '../config/constants';

/**
 * Resolves a movie title (with optional year) to its TMDB id, poster URL, and
 * canonical title. Checks Supabase cache first; falls back to TMDB search.
 */
export const fetchMovieId = async (movieTitleOrId, currentActor = null) => {
  try {
    if (!isNaN(movieTitleOrId)) {
      return { id: movieTitleOrId, poster: null };
    }

    const titleMatch = movieTitleOrId.match(/^(.*?)\s*\((\d{4})\)$/);
    const movieTitle = titleMatch ? titleMatch[1].trim() : movieTitleOrId.trim();
    const releaseYear = titleMatch ? titleMatch[2] : null;

    const exactLabel = releaseYear ? `${movieTitle} (${releaseYear})` : movieTitle;

    let query = supabase
      .from('actor_movie_connections')
      .select('movie_id, movie_poster, movie_name')
      .eq('movie_name', exactLabel);

    if (currentActor) {
      query = query.eq('actor_name', currentActor);
    }

    const { data: cachedMovie, error: cacheError } = await query.limit(1);

    if (cacheError) {
      console.error("❌ Supabase cache error:", cacheError);
    }

    if (cachedMovie && cachedMovie.length > 0) {
      const cachedRow = cachedMovie[0];
      if (cachedRow.movie_poster) {
        return {
          id: cachedRow.movie_id,
          poster: cachedRow.movie_poster?.startsWith("http")
            ? cachedRow.movie_poster
            : `${TMDB_IMG_BASE}${cachedRow.movie_poster}`,
          titleWithYear: cachedRow.movie_name,
        };
      }
    }

    const url = `${TMDB_BASE_URL}/search/movie?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(movieTitle)}${releaseYear ? `&year=${releaseYear}` : ""}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!data.results || data.results.length === 0) {
      return null;
    }

    let exactMatches = data.results.filter(
      (movie) =>
        movie.title.toLowerCase() === movieTitle.toLowerCase() &&
        (!releaseYear || movie.release_date?.startsWith(releaseYear))
    );

    let bestMatch;
    if (exactMatches.length === 1) {
      bestMatch = exactMatches[0];
    } else if (exactMatches.length > 1 && currentActor) {
      bestMatch = await disambiguateByActor(exactMatches, currentActor);
    }

    if (!bestMatch) {
      bestMatch =
        data.results.find((m) => m.title.toLowerCase() === movieTitle.toLowerCase()) ||
        data.results.find((m) => m.release_date?.startsWith(releaseYear)) ||
        data.results[0];
    }

    if (!bestMatch?.id) return null;

    return {
      id: bestMatch.id,
      poster: bestMatch.poster_path ? `${TMDB_IMG_BASE}${bestMatch.poster_path}` : null,
      titleWithYear: `${bestMatch.title} (${bestMatch.release_date?.slice(0, 4)})`,
    };
  } catch (error) {
    console.error("❌ Error fetching movie ID:", error);
    return null;
  }
};

/**
 * When multiple movies share the same title + year, picks the one that
 * actually has the given actor in its cast.
 */
export const disambiguateByActor = async (movies, actorName) => {
  for (const movie of movies) {
    const res = await fetch(
      `${TMDB_BASE_URL}/movie/${movie.id}/credits?api_key=${TMDB_API_KEY}`
    );
    const credits = await res.json();
    const found = credits.cast?.some(
      (c) => c.name?.toLowerCase() === actorName.toLowerCase()
    );
    if (found) return movie;
  }
  return null;
};

/** Fetches a movie's full details (title, poster) by TMDB movie ID. */
export const fetchMovieDetailsById = async (movieId) => {
  try {
    const { data: cachedRows, error: cacheError } = await supabase
      .from('actor_movie_connections')
      .select('movie_name, movie_id, movie_poster')
      .eq('movie_id', movieId);

    if (cacheError) console.error('❌ Supabase cache fetch error:', cacheError);

    if (cachedRows && cachedRows.length > 0) {
      const cached = cachedRows.find((r) => r.movie_poster) || cachedRows[0];
      return {
        title: cached.movie_name,
        poster: cached.movie_poster,
        id: cached.movie_id,
      };
    }

    const url = `${TMDB_BASE_URL}/movie/${movieId}?api_key=${TMDB_API_KEY}`;
    const response = await fetch(url);
    const movie = await response.json();

    return {
      title: movie.title
        ? `${movie.title} (${movie.release_date?.slice(0, 4)})`
        : null,
      poster: movie.poster_path ? `${TMDB_IMG_BASE}${movie.poster_path}` : null,
      id: movie.id,
    };
  } catch (err) {
    console.error('❌ Error fetching movie details:', err);
    return { title: null, poster: null, id: movieId };
  }
};

/** Fetches an actor's profile image URL by name. Checks Supabase cache first. */
export const fetchActorProfile = async (actorName) => {
  try {
    const { data: cached } = await supabase
      .from('actor_movie_connections')
      .select('actor_image')
      .eq('actor_name', actorName)
      .not('actor_image', 'is', null)
      .limit(1);

    if (cached && cached.length > 0) return cached[0].actor_image;

    const url = `${TMDB_BASE_URL}/search/person?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(actorName)}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.results && data.results.length > 0) {
      return `${TMDB_IMG_BASE}${data.results[0].profile_path}`;
    }
    return null;
  } catch (error) {
    console.error("❌ Error fetching actor profile:", error);
    return null;
  }
};

/** Fetches an actor's profile image URL by TMDB person ID. */
export const fetchActorProfileById = async (actorId) => {
  try {
    const url = `${TMDB_BASE_URL}/person/${actorId}?api_key=${TMDB_API_KEY}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data?.profile_path) {
      return `${TMDB_IMG_BASE}${data.profile_path}`;
    }
    return null;
  } catch (error) {
    console.error("❌ Error fetching actor profile by ID:", error);
    return null;
  }
};

/**
 * Searches TMDB for movies or people matching the query.
 * Filters out documentaries, talk shows, and known non-fiction keywords.
 * Returns up to 15 results sorted by popularity.
 */
export const searchTmdb = async (query, type) => {
  const EXCLUDED_GENRE_IDS = new Set([99, 10767]);
  const EXCLUDED_KEYWORDS = [
    "stand-up", "comedy special", "olympics", "super bowl", "live from",
    "halftime", "concert", "documentary", "special event", "bbc radio 1's", "katy perry",
  ];

  try {
    const endpoint = type === "movie" ? "movie" : "person";
    const url = `${TMDB_BASE_URL}/search/${endpoint}?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!data?.results) return [];

    let results = data.results.map((item) => ({
      label:
        type === "movie"
          ? `${item.title}${item.release_date ? ` (${item.release_date.slice(0, 4)})` : ""}`
          : item.name,
      id: item.id,
      popularity: item.popularity || 0,
      type,
      genre_ids: item.genre_ids || [],
      original_language: item.original_language || null,
    }));

    if (type === "movie") {
      results = results.filter((s) => {
        const titleLower = s.label.toLowerCase();
        const titleWords = new Set(titleLower.split(/\s+/));
        const hasBadGenre = s.genre_ids?.some((id) => EXCLUDED_GENRE_IDS.has(id));
        const hasBadKeyword = EXCLUDED_KEYWORDS.some((kw) => titleWords.has(kw));
        return !(hasBadGenre || hasBadKeyword);
      });
    }

    const seenLabels = new Set();
    const deduped = results.filter((item) => {
      if (!item.label) return false;
      const key = item.label.toLowerCase();
      if (seenLabels.has(key)) return false;
      seenLabels.add(key);
      return true;
    });

    return deduped.sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 15);
  } catch (err) {
    console.error("❌ TMDb search error:", err);
    return [];
  }
};
